import { useEffect, useRef, useState } from "react"

/**
 * OKS-477 — Client-side countdown for the §07 mission-drop hero.
 *
 * Ticks once per second (no per-second server hit — UX Spec §4) toward
 * `targetIso`. When the remaining time reaches zero it fires `onComplete`
 * exactly once (the §07 page uses this to `router.reload({ only: ["drop"] })`
 * so the server re-resolves countdown→revealed). A separate periodic refetch
 * in the page corrects drift + catches a drop that landed while the tab was
 * backgrounded.
 *
 * SSR-safe: the initial value is computed synchronously (deterministic for
 * the Inertia SSR pass) and the interval only runs in the browser via
 * `useEffect`.
 */
export interface Countdown {
  /** Whole hours remaining (can exceed 24 for multi-day drops). */
  hours: number
  minutes: number
  seconds: number
  /** Total milliseconds remaining, clamped at 0. */
  totalMs: number
  /** True once the target has passed (totalMs === 0). */
  isComplete: boolean
}

function computeRemaining(targetIso?: string | null): number {
  if (!targetIso) return 0
  const target = new Date(targetIso).getTime()
  if (Number.isNaN(target)) return 0
  return Math.max(0, target - Date.now())
}

function breakdown(totalMs: number): Omit<Countdown, "totalMs"> {
  const totalSeconds = Math.floor(totalMs / 1000)
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    isComplete: totalMs <= 0,
  }
}

export function useCountdown(
  targetIso?: string | null,
  onComplete?: () => void,
): Countdown {
  const [totalMs, setTotalMs] = useState(() => computeRemaining(targetIso))
  const completedRef = useRef(false)
  // Keep the latest callback without re-arming the interval each render
  // (synced in an effect, never mutated during render).
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    completedRef.current = false

    // Tick immediately, then once per second. The first call re-syncs after a
    // `targetIso` change (without a setState in the effect body); thereafter
    // the interval drives the countdown. `setTotalMs` inside the callback is
    // a callback, not a render-phase update.
    const tick = () => {
      const remaining = computeRemaining(targetIso)
      setTotalMs(remaining)

      if (remaining <= 0 && !completedRef.current) {
        completedRef.current = true
        onCompleteRef.current?.()
        clearInterval(id)
      }
    }

    const id = setInterval(tick, 1000)
    tick()

    return () => clearInterval(id)
  }, [targetIso])

  return { totalMs, ...breakdown(totalMs) }
}
